import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, BadgeIndianRupee, CheckCircle2, CreditCard,
  Loader2, LockKeyhole, ShieldCheck, Sparkles, WalletCards, XCircle
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const RAZORPAY_API_BASE = "/backend/api/razorpay";
const CREATE_ORDER_ENDPOINT = `${RAZORPAY_API_BASE}/create_order.php`;
const VERIFY_PAYMENT_ENDPOINT = `${RAZORPAY_API_BASE}/verify_payment.php`;
const QUICK_AMOUNTS = [100, 200, 500, 1000, 2000, 5000];

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const existing = document.querySelector('script[src="https://checkout.razorpay.com/v1/checkout.js"]');
    if (existing) {
      existing.addEventListener("load", () => resolve(true), { once: true });
      existing.addEventListener("error", () => resolve(false), { once: true });
      return;
    }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

async function readJson(response) {
  const text = await response.text();
  if (!text.trim()) throw new Error(`Server returned an empty response. HTTP ${response.status}.`);
  try {
    return JSON.parse(text);
  } catch {
    console.error("Invalid Razorpay response:", text);
    throw new Error("Server returned an invalid response. Check the PHP error log.");
  }
}

export default function WalletTopUp() {
  const navigate = useNavigate();
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingText, setLoadingText] = useState("");
  const [scriptReady, setScriptReady] = useState(false);
  const [scriptError, setScriptError] = useState("");
  const [paymentState, setPaymentState] = useState("idle");
  const [paymentId, setPaymentId] = useState("");

  useEffect(() => {
    let active = true;
    loadRazorpayScript().then((ok) => {
      if (!active) return;
      setScriptReady(ok);
      setScriptError(ok ? "" : "Unable to load Razorpay Checkout. Check your internet connection.");
    });
    return () => { active = false; };
  }, []);

  const numericAmount = Number(amount);
  const validAmount = Number.isFinite(numericAmount) && numericAmount >= 1;
  const formattedAmount = useMemo(() => {
    if (!validAmount) return "₹0";
    return new Intl.NumberFormat("en-IN", {
      style: "currency", currency: "INR", maximumFractionDigits: 2
    }).format(numericAmount);
  }, [numericAmount, validAmount]);

  const selectAmount = (value) => {
    if (loading) return;
    setAmount(String(value));
    setPaymentState("idle");
    setPaymentId("");
  };

  const handleAmountChange = (event) => {
    const value = event.target.value;
    if (value === "" || /^\d*(\.\d{0,2})?$/.test(value)) {
      setAmount(value);
      setPaymentState("idle");
      setPaymentId("");
    }
  };

  const verifyPayment = async (rzpResponse) => {
    setLoading(true);
    setLoadingText("Verifying payment...");
    setPaymentState("verifying");

    const response = await fetch(VERIFY_PAYMENT_ENDPOINT, {
      method: "POST",
      credentials: "include",
      cache: "no-store",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({
        razorpay_payment_id: rzpResponse.razorpay_payment_id,
        razorpay_order_id: rzpResponse.razorpay_order_id,
        razorpay_signature: rzpResponse.razorpay_signature,
      }),
    });

    const result = await readJson(response);

    if (response.status === 401) {
      toast.error(result.message || "Please login again.");
      navigate("/login", { replace: true });
      return;
    }

    if (!response.ok || !result.success) {
      throw new Error(result.message || "Payment verification failed.");
    }

    setPaymentId(result.payment_id || rzpResponse.razorpay_payment_id || "");
    setPaymentState("success");
    toast.success(result.message || "Wallet updated successfully.");

    /*
     * After successful server verification and wallet credit,
     * automatically return to the React dashboard.
     */
    setTimeout(() => {
      navigate("/dashboard", { replace: true });
    }, 1800);
  };

  const startPayment = async () => {
    if (loading) return;
    if (!scriptReady || !window.Razorpay) {
      toast.error(scriptError || "Razorpay Checkout is not ready.");
      return;
    }
    if (!validAmount) {
      toast.error("Please enter an amount of at least ₹1.");
      return;
    }

    setLoading(true);
    setLoadingText("Creating secure order...");
    setPaymentState("creating");
    setPaymentId("");

    try {
      const orderResponse = await fetch(CREATE_ORDER_ENDPOINT, {
        method: "POST",
        credentials: "include",
        cache: "no-store",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ amount: numericAmount }),
      });

      const order = await readJson(orderResponse);

      if (orderResponse.status === 401) {
        toast.error(order.message || "Please login again.");
        navigate("/login", { replace: true });
        return;
      }

      if (!orderResponse.ok || !order.success) {
        throw new Error(order.message || "Unable to create payment order.");
      }

      const checkout = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency || "INR",
        name: "Smart Solutions",
        description: "Wallet Top-up",
        order_id: order.order_id,
        notes: { purpose: "Wallet Top-up" },
        handler: async (response) => {
          try {
            await verifyPayment(response);
          } catch (error) {
            console.error("Razorpay verification error:", error);
            setPaymentState("failed");
            toast.error(error instanceof Error ? error.message : "Payment verification failed.");
          } finally {
            setLoading(false);
            setLoadingText("");
          }
        },
        modal: {
          ondismiss: () => {
            setPaymentState("cancelled");
            setLoading(false);
            setLoadingText("");
            toast.info("Payment was cancelled.");
          },
        },
        theme: { color: "#2563eb" },
      });

      checkout.on("payment.failed", (response) => {
        const message =
          response?.error?.description ||
          response?.error?.reason ||
          "Payment failed.";
        setPaymentState("failed");
        setLoading(false);
        setLoadingText("");
        toast.error(message);
      });

      checkout.open();
      setLoading(false);
      setLoadingText("");
      setPaymentState("checkout");
    } catch (error) {
      console.error("Razorpay order error:", error);
      setPaymentState("failed");
      toast.error(error instanceof Error ? error.message : "Unable to start payment.");
      setLoading(false);
      setLoadingText("");
    }
  };

  if (paymentState === "success") {
    return (
      <div className="min-h-screen bg-background px-4 py-8 sm:px-6">
        <div className="mx-auto max-w-xl">
          <Card className="overflow-hidden rounded-3xl border">
            <CardContent className="p-7 text-center sm:p-10">
              <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-emerald-500/10">
                <CheckCircle2 className="h-11 w-11 text-emerald-600" />
              </div>
              <h1 className="mt-6 text-3xl font-bold">Wallet Updated</h1>
              <p className="mt-3 text-muted-foreground">
                Your payment was verified successfully and your wallet was credited.
                Redirecting to dashboard...
              </p>
              <div className="mt-6 rounded-2xl border bg-muted/30 p-5">
                <p className="text-sm text-muted-foreground">Amount Added</p>
                <p className="mt-1 text-3xl font-bold">{formattedAmount}</p>
                {paymentId && (
                  <div className="mt-4 border-t pt-4">
                    <p className="text-xs text-muted-foreground">Razorpay Payment ID</p>
                    <p className="mt-1 break-all font-mono text-xs">{paymentId}</p>
                  </div>
                )}
              </div>
              <div className="mt-7 grid gap-3 sm:grid-cols-2">
                <Button type="button" variant="outline" onClick={() => {
                  setPaymentState("idle"); setPaymentId(""); setAmount("");
                }}>
                  Add More Money
                </Button>
                <Button type="button" onClick={() => navigate("/dashboard", { replace: true })}>
                  Go to Dashboard
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background px-4 py-6 sm:px-6 lg:py-10">
      <div className="mx-auto max-w-5xl">
        <div className="mb-6 flex items-center justify-between gap-4">
          <Button type="button" variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="mr-2 h-4 w-4" /> Back
          </Button>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <LockKeyhole className="h-4 w-4" /> Secure Payment
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-[1.15fr_0.85fr]">
          <Card className="overflow-hidden rounded-3xl border">
            <CardHeader className="border-b p-6 sm:p-8">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10">
                <WalletCards className="h-6 w-6 text-primary" />
              </div>
              <CardTitle className="text-3xl">Add Money to Wallet</CardTitle>
              <p className="text-sm text-muted-foreground sm:text-base">
                Add funds securely using Razorpay. Wallet credit happens only after server verification.
              </p>
            </CardHeader>

            <CardContent className="space-y-7 p-6 sm:p-8">
              <div>
                <Label htmlFor="walletAmount">Amount</Label>
                <div className="relative mt-2">
                  <BadgeIndianRupee className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="walletAmount"
                    value={amount}
                    onChange={handleAmountChange}
                    inputMode="decimal"
                    placeholder="Enter amount"
                    disabled={loading}
                    className="h-14 pl-12 text-xl font-semibold"
                    autoComplete="off"
                  />
                </div>
                <p className="mt-2 text-xs text-muted-foreground">Minimum wallet top-up: ₹1</p>
              </div>

              <div>
                <p className="mb-3 text-sm font-medium">Quick Amount</p>
                <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
                  {QUICK_AMOUNTS.map((value) => (
                    <Button
                      key={value}
                      type="button"
                      size="sm"
                      variant={Number(amount) === value ? "default" : "outline"}
                      onClick={() => selectAmount(value)}
                      disabled={loading}
                      className="h-10"
                    >
                      ₹{value}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="rounded-2xl border bg-muted/20 p-5">
                <div className="flex items-center justify-between gap-4">
                  <span className="text-sm text-muted-foreground">Wallet Top-up</span>
                  <span className="font-semibold">{formattedAmount}</span>
                </div>
                <div className="my-4 border-t" />
                <div className="flex items-center justify-between gap-4">
                  <span className="font-medium">Payable Amount</span>
                  <span className="text-xl font-bold">{formattedAmount}</span>
                </div>
              </div>

              {scriptError && (
                <div className="rounded-2xl border border-red-500/40 bg-red-500/5 p-4 text-sm text-red-600">
                  {scriptError}
                </div>
              )}

              {paymentState === "failed" && (
                <Status icon={XCircle} title="Payment not completed"
                  text="Your wallet has not been credited. You can try again." error />
              )}

              {paymentState === "cancelled" && (
                <Status icon={XCircle} title="Payment cancelled"
                  text="No money was added to your wallet." />
              )}

              <Button
                type="button"
                onClick={startPayment}
                disabled={loading || !validAmount || !scriptReady}
                className="h-12 w-full text-base font-semibold"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    {loadingText || "Processing..."}
                  </>
                ) : (
                  <>
                    <CreditCard className="mr-2 h-5 w-5" />
                    Pay {validAmount ? formattedAmount : ""} with Razorpay
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          <div className="space-y-5">
            <Card className="rounded-3xl border">
              <CardContent className="p-6 sm:p-7">
                <div className="flex items-start gap-4">
                  <div className="rounded-2xl bg-primary/10 p-3">
                    <ShieldCheck className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h2 className="font-semibold">Secure Wallet Credit</h2>
                    <p className="mt-1 text-sm leading-6 text-muted-foreground">
                      PHP creates the Razorpay order, verifies the payment signature, and credits the wallet.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-3xl border">
              <CardContent className="p-6 sm:p-7">
                <div className="mb-5 flex items-center gap-2">
                  <Sparkles className="h-5 w-5" />
                  <h2 className="font-semibold">Payment Process</h2>
                </div>
                <div className="space-y-5">
                  <Step number="1" title="Enter amount" text="Choose a quick amount or enter a custom amount." />
                  <Step number="2" title="Pay securely" text="Complete payment in Razorpay Checkout." />
                  <Step number="3" title="Server verification" text="PHP validates the Razorpay signature." />
                  <Step number="4" title="Wallet updated" text="Only verified payments are credited." />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}

function Step({ number, title, text }) {
  return (
    <div className="flex gap-3">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border bg-muted text-sm font-semibold">
        {number}
      </div>
      <div>
        <p className="font-medium">{title}</p>
        <p className="mt-1 text-sm leading-5 text-muted-foreground">{text}</p>
      </div>
    </div>
  );
}

function Status({ icon: Icon, title, text, error = false }) {
  return (
    <div className={`rounded-2xl border p-4 ${error ? "border-red-500/40 bg-red-500/5 text-red-600" : "bg-muted/30 text-muted-foreground"}`}>
      <div className="flex items-start gap-3">
        <Icon className="mt-0.5 h-5 w-5 shrink-0" />
        <div>
          <p className="font-medium">{title}</p>
          <p className="mt-1 text-sm">{text}</p>
        </div>
      </div>
    </div>
  );
}
